import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Select,
  Input,
  DatePicker,
  TimePicker,
  Button,
  notification,
  Spin,
  Space,
  InputNumber,
  Row,
  Col,
  Divider,
  Alert
} from 'antd';
import { PlusOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import * as workService from './workTrackingService';
import { getCurrentStaffId } from '../../authHelper';

const { Option } = Select;
const { TextArea } = Input;

const AddManualActivity = ({ onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [subtypes, setSubtypes] = useState([]);
  const [manualFields, setManualFields] = useState({});
  const [currentStaffId, setCurrentStaffId] = useState(null);

   useEffect(() => {
    const staffId = localStorage.getItem("staffId");
    setCurrentStaffId(staffId ? parseInt(staffId) : null);
    loadCategories();
  }, []);


  const loadCategories = async () => {
    try {
      const res = await workService.getWorkCategories();
      setCategories(res.data);
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to load categories'
      });
    }
  };

  const handleCategoryChange = (value) => {
    const category = categories.find(c => c.name === value);
    setSelectedCategory(category);
    setSubtypes(category?.subtypes || []);
    setManualFields(category?.manual_fields || {});

    // Reset dependent fields
    form.setFieldsValue({
      activity_type: undefined,
      work_details: {}
    });
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      // Format dates and times
      const startDate = values.start_date.format('YYYY-MM-DD');
      const startTime = values.start_time.format('HH:mm:ss');
      const endTime = values.end_time.format('HH:mm:ss');

      const activityData = {
        staff_id: currentStaffId,
        activity_category: values.activity_category,
        activity_type: values.activity_type,
        description: values.description,
        start_time: `${startDate} ${startTime}`,
        end_time: `${startDate} ${endTime}`,
        work_details: values.work_details || {}
      };

      await workService.addManualActivity(activityData);

      notification.success({
        message: 'Success',
        description: 'Activity added successfully!'
      });

      form.resetFields();
      if (onSuccess) { onSuccess(); } else { history.push('/work-tracking'); }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: error.message || 'Failed to add activity'
      });
    } finally {
      setLoading(false);
    }
  };

  const renderManualFields = () => {
    if (!manualFields || Object.keys(manualFields).length === 0) {
      return null;
    }

    return (
      <>
        <Divider>Work Details</Divider>
        <Row gutter={16}>
          {Object.entries(manualFields).map(([fieldKey, fieldConfig]) => {
            const fieldName = ['work_details', fieldKey];

            if (fieldConfig.type === 'number') {
              return (
                <Col xs={24} md={12} key={fieldKey}>
                  <Form.Item
                    label={fieldConfig.label}
                    name={fieldName}
                    rules={[{ required: fieldConfig.required, message: `${fieldConfig.label} is required` }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder={fieldConfig.label}
                      min={0}
                    />
                  </Form.Item>
                </Col>
              );
            } else if (fieldConfig.type === 'textarea') {
              return (
                <Col xs={24} key={fieldKey}>
                  <Form.Item
                    label={fieldConfig.label}
                    name={fieldName}
                    rules={[{ required: fieldConfig.required, message: `${fieldConfig.label} is required` }]}
                  >
                    <TextArea
                      rows={3}
                      placeholder={fieldConfig.label}
                    />
                  </Form.Item>
                </Col>
              );
            } else {
              return (
                <Col xs={24} md={12} key={fieldKey}>
                  <Form.Item
                    label={fieldConfig.label}
                    name={fieldName}
                    rules={[{ required: fieldConfig.required, message: `${fieldConfig.label} is required` }]}
                  >
                    <Input placeholder={fieldConfig.label} />
                  </Form.Item>
                </Col>
              );
            }
          })}
        </Row>
      </>
    );
  };

  const formContent = (
    <>
      <Alert
        message="Add activities that weren't automatically tracked"
        description="Use this form to log work like phone calls, field visits, photo/video editing, and other manual tasks."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          start_date: moment(),
          start_time: moment().subtract(2, 'hours'),
          end_time: moment()
        }}
      >
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item label="Work Category" name="activity_category" rules={[{ required: true, message: 'Please select a category' }]}>
              <Select placeholder="Select category" size="large" onChange={handleCategoryChange}>
                {categories.filter(cat => !cat.auto_tracked || cat.auto_tracked === false).map(cat => (
                  <Option key={cat.key} value={cat.name}>{cat.icon} {cat.name}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Activity Type" name="activity_type" rules={[{ required: true, message: 'Please select activity type' }]}>
              <Select placeholder="Select activity type" size="large" disabled={subtypes.length === 0}>
                {subtypes.map(subtype => <Option key={subtype} value={subtype}>{subtype}</Option>)}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Description" name="description" rules={[{ required: true, message: 'Please provide a description' }]}>
          <TextArea rows={4} placeholder="Describe what work you did..." />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item label="Date" name="start_date" rules={[{ required: true, message: 'Please select date' }]}>
              <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" disabledDate={(current) => current && current > moment().endOf('day')} />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item label="Start Time" name="start_time" rules={[{ required: true, message: 'Please select start time' }]}>
              <TimePicker style={{ width: '100%' }} format="HH:mm" minuteStep={5} />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item label="End Time" name="end_time" rules={[{ required: true, message: 'Please select end time' }]}>
              <TimePicker style={{ width: '100%' }} format="HH:mm" minuteStep={5} />
            </Form.Item>
          </Col>
        </Row>

        {renderManualFields()}

        <Form.Item style={{ marginTop: 24 }}>
          <Space>
            <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={loading} size="large"
              style={{ background: '#7a0000', borderColor: '#7a0000' }}>
              Add Activity
            </Button>
            <Button onClick={() => onCancel ? onCancel() : history.goBack()} size="large">Cancel</Button>
          </Space>
        </Form.Item>
      </Form>
    </>
  );

  if (onSuccess) {
    return <div style={{ padding: '0 4px' }}>{formContent}</div>;
  }

  return (
    <div className="add-manual-activity">
      <Card
        title={
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => history.goBack()} />
            Add Manual Work Entry
          </Space>
        }
      >
        {formContent}
      </Card>
    </div>
  );
};

export default AddManualActivity;
